import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Product } from '../../model/Products';
import { getSellingPoints } from './productInfo';

interface Props {
	targetProducts: Product[];
	initialSelectedProduct: Product | undefined;
	setSelectedProduct: (product: Product) => void;
	rowUpdated: any;
}

export const B2B2ProductDropdown: React.FC<Props> = (props: Props) => {
	const [isOpen, setIsOpen] = useState(false);
	const [selectedOption, setSelectedOption] = useState(props.initialSelectedProduct ? props.initialSelectedProduct : props.targetProducts[1]);

	const toggling = () => setIsOpen(!isOpen);
	const onOptionClicked = (value: Product) => () => {
		setSelectedOption(value);
		setIsOpen(false);
		props.setSelectedProduct(value);
		props.rowUpdated();
	};

	const componentRef = useRef<null | HTMLDivElement>(null);

	useEffect(() => {
		document.addEventListener('click', handleClick);
		return () => document.removeEventListener('click', handleClick);
		function handleClick(e: any) {
			if (componentRef && componentRef.current) {
				const ref: any = componentRef.current;
				if (!ref.contains(e.target)) {
					setIsOpen(false);
				}
			}
		}
	}, []);

	return (
		<DropDownContainer ref={componentRef as any}>
			<DropDownHeader onClick={toggling}>
				<DropDownSelect>
					<div className="col-name">{selectedOption.displayName}</div>
					<div className="col-price">{selectedOption.eurPerMonth}{selectedOption.maxEurPerMonth && (" - " + selectedOption.maxEurPerMonth)} €/kk</div>
					<div className="arrow">
						<svg width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M1.35355 0.646447C1.15829 0.451184 0.841709 0.451184 0.646447 0.646447C0.451184 0.841709 0.451184 1.15829 0.646447 1.35355L1.35355 0.646447ZM7 7L6.64645 7.35355L7 7.70711L7.35355 7.35355L7 7ZM13.3536 1.35355C13.5488 1.15829 13.5488 0.841709 13.3536 0.646447C13.1583 0.451184 12.8417 0.451184 12.6464 0.646447L13.3536 1.35355ZM0.646447 1.35355L6.64645 7.35355L7.35355 6.64645L1.35355 0.646447L0.646447 1.35355ZM7.35355 7.35355L13.3536 1.35355L12.6464 0.646447L6.64645 6.64645L7.35355 7.35355Z" fill="#4C4C4C"/>
						</svg>
					</div>
				</DropDownSelect>
				<SellingPoints className="selling-points">
					{getSellingPoints(selectedOption.orderItemCode).map((item: any, index) => (
						<span key={index}>{item}</span>
					))}
				</SellingPoints>
			</DropDownHeader>
			{isOpen && (
				<DropDownListContainer>
					<DropDownList>
						{props.targetProducts.map((product: Product) => (
							<ListItem onClick={onOptionClicked(product)} key={product.orderItemCode} className={product.displayName === selectedOption.displayName ? 'active' : ''}>
								<div className="list-item-header">
									<div className="col-info">
										<div className="subs-speed">{product.dataSpeedMbs} Mbit</div>
										<div className="subs-name">{product.displayName}</div>
									</div>
									<div className="col-price">{product.eurPerMonth}{product.maxEurPerMonth && (" - " + product.maxEurPerMonth)} €/kk</div>
								</div>
								<SellingPoints className="selling-points">
									{getSellingPoints(product.orderItemCode).map((item: any, index) => (
										<span key={index}>
											{item}
										</span>
									))}
								</SellingPoints>
							</ListItem>
						))}
					</DropDownList>
				</DropDownListContainer>
			)}
		</DropDownContainer>
	);
};

const DropDownContainer = styled('div')`
	position: relative;
	width: 100%;
	margin: 0 auto;
`;

const DropDownHeader = styled('div')`
	.selling-points {
		color: #fff;
	}
`;

const DropDownSelect = styled('div')`
	position: relative;
	font-size: 18px;
	display: flex;
	justify-content: space-between;
	align-items: center;
	background-color: #fff;
	padding: 0.4em 52px 0.4em 10px;
	box-shadow: 0 2px 3px rgba(0, 0, 0, 0.15);
	margin-bottom: 10px;
	
	.arrow {
		display: flex;
		align-items: center;
		justify-content: center;
		position: absolute;
		right: 0;
		top: 0;
		height: 100%;
		width: 42px;
		border-left: 1px solid #D7D7D7;
	}

	.col-price {
		font-weight: 500;
		font-size: 18px;
		width: 140px;
		text-align: right;
	}

	.col-name {
		flex: 0 1 100%;
		font-weight: 500;
	}
`;

const SellingPoints = styled('div')`
	font-size: 16px;
	line-height: 1.25;
	font-weight: bold;
	
	span br {
		content: " ";
		
		&:after {
			content: " ";
		}
	}
	
	span:after {
		content: ', ';
	}
	
	span:last-of-type:after {
		content: '';
	}
`;

const DropDownListContainer = styled('div')`
	position: absolute;
	top: 40px;
	z-index: 100;
	width: 100%;
	
	@media screen and (min-width: 800px) {
		width: 150%;
		max-width: 500px;
		right: 0;
	}
`;

const DropDownList = styled('ul')`
	padding: 0;
	margin: 2px 0 0;
	background: #ffffff;
	border: 1px solid #e5e5e5;
	box-sizing: border-box;
	color: #4c4c4c;
	box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
`;

const ListItem = styled('li')`
	list-style: none;
	position: relative;
	margin: 0;
	padding: 0.75rem 1rem;
	border-bottom: 1px solid #e5e5e5;
	
	&:hover {
		background-color: #fafafa;
		cursor: pointer;
	}

	&.active {
		background-color: #fafafa;
		box-shadow: 0px 0px 0px 1px #99cc00;

		.col-info {
			.subs-name {
				color: #000;
			}
		}
		.col-price {
			color: #000;
		}
	}
	
	.list-item-header {
		display: flex;
		justify-content: space-between;
		align-items: flex-end;
		margin-bottom: 5px;
	}

	.col-info {
		flex: 0 1 100%;
		line-height: 1.3;

		.subs-speed {
			color: #ff0000;
			font-size: 16px;
		}
		.subs-name {
			font-weight: 500;
			font-size: 18px;
		}
	}

	.col-price {
		width: 140px;
		text-align: right;
		font-weight: 500;
	}
`;
